import {
  LinkButton,
  List,
  ListItem,
  Loader,
  SectionMessage,
} from "@certn/cascadia-react";
import { APIError, fetcher } from "@certn/api";
import { Link, Stack, Text, showToast } from "@certn/cascadia";
import { BaseLayout } from "@certn/forms/src/layouts/BaseLayout";
import { css } from "@emotion/css";
import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { getPrivacyPolicyUrl } from "@certn/utils";
import { ArrayControlProps } from "@jsonforms/core";

import { useApplicationFormContext } from "../../ApplicationFormContext";
import { useKonfirVerification } from "./hooks/useKonfirVerification";
import { EmploymentVerificationComplete } from "./EmploymentVerificationComplete";
import { EmploymentVerificationNavigation } from "./EmploymentVerificationNavigation";
import {
  deriveStateFromKonfirStatus,
  getKonfirError,
  isTerminalKonfirStatus,
  openKonfirWindow,
  resolvePostKonfirSkipDestination,
} from "./helpers";
import { useApplication } from "../../ApplicationContext";
import { EmploymentVerificationGetStartedModal } from "./EmploymentVerificationGetStartedModal";
import { EmploymentVerificationUnableToVerifyModal } from "./EmploymentVerificationUnableToVerifyModal";

export function EmploymentVerificationControl(props: ArrayControlProps) {
  const { t, i18n } = useTranslation();
  const { caseId } = useApplicationFormContext();
  const { navigateToSection, refresh, basePath } = useApplication();

  const [showGetStarted, setShowGetStarted] = useState(false);
  const [isMiniBrowserOpen, setIsMiniBrowserOpen] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);

  const options = props.uischema?.options || {};
  const isDigitalVerificationEnabled =
    options.digitalVerificationEnabled === true;
  const employmentDocumentsFallbackAvailable =
    options.employmentDocumentsFallbackAvailable === true;
  const language = i18n.language.split("-")[0];
  const privacyPolicyUrl = getPrivacyPolicyUrl(language);

  const {
    konfirVerificationId,
    status,
    setStatus,
    isStarting,
    isStatusLoading,
    startVerification,
  } = useKonfirVerification(caseId);

  const { isRecoverable, isInProgress, isInitial, isSkipped } =
    deriveStateFromKonfirStatus(status);

  const showUnableToVerifyModal =
    status === "FAILED" &&
    employmentDocumentsFallbackAvailable &&
    !showGetStarted;

  useEffect(() => {
    if (!isTerminalKonfirStatus(status)) {
      return;
    }

    setShowGetStarted(false);
    setIsMiniBrowserOpen(false);
  }, [status]);

  const skipKonfir = useCallback(
    async ({ reason }: { reason: string }) => {
      setIsSkipping(true);
      try {
        const verificationsBasePath = `/api/internal/applicant/case/${caseId}/konfir/verifications`;
        const url = konfirVerificationId
          ? `${verificationsBasePath}/${konfirVerificationId}/bypass/`
          : `${verificationsBasePath}/bypass/`;

        await fetcher.submit(url, { reason });
        setStatus("BYPASSED");
        await refresh();
        const destination = await resolvePostKonfirSkipDestination(
          caseId,
          basePath
        );
        navigateToSection(destination);

        return true;
      } catch (error) {
        console.error("Error bypassing Konfir verification:", error);
        showToast(
          t(
            "We couldn't skip instant verification right now. You can keep going — we'll verify your employment manually."
          ),
          { type: "error" }
        );
        return false;
      } finally {
        setIsSkipping(false);
      }
    },
    [
      basePath,
      caseId,
      konfirVerificationId,
      navigateToSection,
      refresh,
      setStatus,
      t,
    ]
  );

  const onStartClick = () => {
    setIsMiniBrowserOpen(false);
    setShowGetStarted(true);
  };

  const onConfirmStart = async () => {
    try {
      const result = await startVerification();
      const url = result?.candidate_url;

      if (url) {
        openKonfirWindow(url);
        setIsMiniBrowserOpen(true);
      } else {
        showToast(t("An error occurred. Please try again later."), {
          type: "error",
        });
      }
    } catch (error: unknown) {
      console.error("Konfir verification start failed:", error);

      if (error instanceof APIError && error.status === 502) {
        showToast(
          t(
            "Instant verification is temporarily unavailable. Please try again later or upload supporting documents."
          ),
          { type: "error" }
        );
        return;
      }

      showToast(
        getKonfirError(error, t("An error occurred. Please try again later.")),
        { type: "error" }
      );
    }
  };

  const onSkipClick = () => {
    setShowGetStarted(false);
    void skipKonfir({
      reason: t("Skipped by applicant."),
    });
  };

  const onSkipAfterFailure = () => {
    void skipKonfir({
      reason: t("Skipped by applicant after Konfir verification failed."),
    });
  };

  if (!isDigitalVerificationEnabled) return null;

  if (isStatusLoading) {
    return (
      <BaseLayout {...props} className={styles.layout} direction="column">
        <div data-testid="konfir-status-loading">
          <Loader height="400px" />
        </div>
      </BaseLayout>
    );
  }

  if (status === "COMPLETED") {
    return (
      <BaseLayout {...props} className={styles.layout} direction="column">
        <EmploymentVerificationComplete />
        <EmploymentVerificationNavigation
          status={status}
          onStartClick={onStartClick}
          isStarting={isStarting}
        />
      </BaseLayout>
    );
  }

  return (
    <BaseLayout {...props} className={styles.layout} direction="column">
      <div className={styles.heading}>
        <Text
          as="h1"
          style="form/page-title"
          testID="employment-verification-title"
        >
          {t("Employment Verification")}
        </Text>
        <Text
          style="form/body-primary"
          color="text-weak"
          testID="employment-verification-subtitle"
        >
          {t(
            "Verify your recent UK employment instantly using Konfir. This step uses the employment history you've provided, so make sure it's complete before continuing."
          )}
        </Text>
      </div>
      {(isInitial || isInProgress || isRecoverable) && (
        <Stack>
          <Stack gap="space-2">
            <Text style="form/section-heading">{t("What to expect")}</Text>
            <List type="unordered">
              <ListItem>
                <Text style="global/body-primary">
                  <Trans>
                    We'll attempt to verify your UK employment from the past 6
                    years
                  </Trans>
                </Text>
              </ListItem>
              <ListItem>
                <Text style="global/body-primary">
                  <Trans>
                    Verified employers and dates will be added automatically
                  </Trans>
                </Text>
              </ListItem>
              <ListItem>
                <Text style="global/body-primary">
                  <Trans>
                    If we can't verify something instantly, we'll continue
                    verifying it using other methods
                  </Trans>
                </Text>
              </ListItem>
            </List>
          </Stack>
          <SectionMessage
            testID="konfir-privacy-callout"
            type="success"
            icon="verified_user"
            sectionTitle={t("Your data and privacy")}
          >
            {t(
              "Certn has partnered with Konfir to securely verify your employment. Only relevant data is shared."
            )}
          </SectionMessage>
          <Stack gap="space-2">
            <Text
              style="global/body-secondary"
              testID="konfir-disclosure-1"
              color="text-weak"
            >
              {t(
                `By clicking "Start verification", you agree to securely share your employment details with our verification partner, Konfir, to attempt instant verification.`
              )}
            </Text>
            <Text
              style="global/body-secondary"
              testID="konfir-disclosure-2"
              color="text-weak"
            >
              <Trans
                components={{
                  policy: (
                    <Link
                      linkStyle="monochrome"
                      testID="privacy-policy-link"
                      href={privacyPolicyUrl}
                      target="_blank"
                    />
                  ),
                }}
              >
                {
                  "Please see our <policy>Privacy Policy</policy> for more information about Certn's privacy practices and your rights."
                }
              </Trans>
            </Text>
            <Text
              style="global/body-secondary"
              testID="konfir-disclosure-3"
              color="text-weak"
            >
              <Trans
                components={{
                  skip: (
                    <LinkButton
                      testID="konfir-bypass-link"
                      linkStyle="monochrome"
                      textStyle="global/body-secondary"
                      onPress={onSkipClick}
                      isDisabled={isSkipping}
                    />
                  ),
                }}
              >
                {"<skip>Click here</skip> if you'd prefer to skip this step."}
              </Trans>
            </Text>
          </Stack>
        </Stack>
      )}
      {isSkipped && (
        <SectionMessage
          testID="konfir-skipped-alert"
          type="warning"
          icon="warning"
          sectionTitle={t("Instant verification skipped")}
        >
          <Trans
            components={{
              a: (
                <LinkButton
                  testID="konfir-retry-link"
                  linkStyle="monochrome"
                  textStyle="global/body-secondary"
                  onPress={onStartClick}
                />
              ),
            }}
          >
            {
              "You skipped this step. We'll verify your employment using another method instead. If you've changed your mind, you can <a>retry instant verification</a>."
            }
          </Trans>
        </SectionMessage>
      )}
      <EmploymentVerificationNavigation
        status={status}
        onStartClick={onStartClick}
        isStarting={isStarting}
      />
      {showUnableToVerifyModal && (
        <EmploymentVerificationUnableToVerifyModal
          onSkipClick={onSkipAfterFailure}
          onRetryClick={onStartClick}
          isSkipping={isSkipping}
        />
      )}
      {showGetStarted && (
        <EmploymentVerificationGetStartedModal
          isStarting={isStarting}
          isMiniBrowserOpen={isMiniBrowserOpen}
          isSkipping={isSkipping}
          onClose={() => setShowGetStarted(false)}
          onSkipClick={onSkipClick}
          onConfirmStart={onConfirmStart}
        />
      )}
    </BaseLayout>
  );
}

const styles = {
  layout: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-4)",
  }),
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
};
